import { supabase, hasSupabase } from './supabase.js';

// ============================================================
// BUYERS
// ============================================================

export async function getBuyerIdForCurrentUser() {
  if (!hasSupabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase.from('buyers').select('id').eq('profile_id', user.id).maybeSingle();
  return data?.id || null;
}

export async function getCatererIdForCurrentUser() {
  if (!hasSupabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase.from('caterers').select('id, verification_status').eq('profile_id', user.id).maybeSingle();
  return data || null;
}

// ============================================================
// BRIEFS
// ============================================================

// Translates app-side brief form (matches MOCK_BRIEFS shape) → DB columns.
function briefFormToDb(form, buyerId) {
  return {
    buyer_id: buyerId,
    title_en: form.title_en || form.title || '',
    title_fr: form.title_fr || null,
    title_nl: form.title_nl || null,
    description_en: form.description_en || form.description || '',
    description_fr: form.description_fr || null,
    description_nl: form.description_nl || null,
    event_type: form.event_type || null,
    event_date: form.event_date,
    event_time: form.event_time || null,
    duration_hours: form.duration_hours || null,
    location: form.location || '',
    guest_count: Number(form.guest_count) || 1,
    budget_min: form.budget_min ? Number(form.budget_min) : null,
    budget_max: form.budget_max ? Number(form.budget_max) : null,
    dietary_requirements: form.dietary_requirements || null,
    service_style: form.service_style || null,
    setup_required: !!form.setup_required,
    staff_required: !!form.staff_required,
    equipment_required: !!form.equipment_required,
    special_requirements: form.special_requirements || null,
    status: form.status || 'open',
    deadline: form.deadline,
  };
}

// Maps DB row → component-friendly shape (the rest of caterbase.jsx expects
// MOCK_BRIEFS keys like `org`, `proposal_count`).
function briefDbToView(row, orgName = null) {
  return {
    ...row,
    id: row.id,
    reference_number: row.reference_number,
    org: orgName || row.organisation_name || '—',
    proposal_count: row.proposal_count ?? 0,
  };
}

export async function fetchMyBriefs() {
  if (!hasSupabase) return [];
  const buyerId = await getBuyerIdForCurrentUser();
  if (!buyerId) return [];
  const { data, error } = await supabase
    .from('briefs')
    .select('*, buyer:buyers!inner(organisation_name)')
    .eq('buyer_id', buyerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(r => briefDbToView(r, r.buyer?.organisation_name));
}

export async function fetchOpenBriefs({ limit = 50 } = {}) {
  if (!hasSupabase) return [];
  const { data, error } = await supabase
    .from('briefs')
    .select('*, buyer:buyers!inner(organisation_name, organisation_type)')
    .eq('status', 'open')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(r => briefDbToView(r, r.buyer?.organisation_name));
}

export async function insertBrief(form) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const buyerId = await getBuyerIdForCurrentUser();
  if (!buyerId) throw new Error('No buyer record for current user');
  const payload = briefFormToDb(form, buyerId);
  const { data, error } = await supabase
    .from('briefs')
    .insert(payload)
    .select('*, buyer:buyers!inner(organisation_name)')
    .single();
  if (error) throw error;
  return briefDbToView(data, data.buyer?.organisation_name);
}

// ============================================================
// PROPOSALS
// ============================================================

function proposalFormToDb(form, briefId, catererId) {
  return {
    brief_id: briefId,
    caterer_id: catererId,
    cover_letter_en: form.cover_letter_en || form.cover_letter || '',
    cover_letter_fr: form.cover_letter_fr || null,
    cover_letter_nl: form.cover_letter_nl || null,
    menu_description: form.menu_description || '',
    price_total: Number(form.price_total) || 0,
    price_per_person: form.price_per_person ? Number(form.price_per_person) : null,
    price_breakdown: form.price_breakdown || null,
    setup_time: form.setup_time || null,
    additional_notes: form.additional_notes || null,
    status: 'submitted',
  };
}

export async function fetchMyProposals() {
  if (!hasSupabase) return [];
  const caterer = await getCatererIdForCurrentUser();
  if (!caterer) return [];
  const { data, error } = await supabase
    .from('proposals')
    .select('*, brief:briefs(title_en, reference_number, status)')
    .eq('caterer_id', caterer.id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchProposalsForBrief(briefId) {
  if (!hasSupabase) return [];
  const { data, error } = await supabase
    .from('proposals')
    .select('*, caterer:caterers(company_name, specialties, avg_rating, description_en)')
    .eq('brief_id', briefId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ============================================================
// ADMIN
// ============================================================

export async function fetchAllCaterers() {
  if (!hasSupabase) return [];
  const { data, error } = await supabase
    .from('caterers')
    .select('id, company_name, vat_number, specialties, verification_status, avg_rating, created_at, rejection_reason, contact_email:profile_id')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function approveCaterer(catererId) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('caterers')
    .update({ verification_status: 'approved', verified_at: new Date().toISOString(), verified_by: user?.id || null, rejection_reason: null })
    .eq('id', catererId)
    .select('*').single();
  if (error) throw error;
  return data;
}

export async function rejectCaterer(catererId, reason) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('caterers')
    .update({ verification_status: 'rejected', rejection_reason: reason || 'No reason provided' })
    .eq('id', catererId)
    .select('*').single();
  if (error) throw error;
  return data;
}

export async function suspendCaterer(catererId, reason) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('caterers')
    .update({ verification_status: 'suspended', rejection_reason: reason || 'Suspended by admin' })
    .eq('id', catererId)
    .select('*').single();
  if (error) throw error;
  return data;
}

export async function fetchAdminMetrics() {
  if (!hasSupabase) return null;
  const [openBriefs, approvedCaterers, pendingCaterers, activeBuyers] = await Promise.all([
    supabase.from('briefs').select('id', { count: 'exact', head: true }).eq('status', 'open'),
    supabase.from('caterers').select('id', { count: 'exact', head: true }).eq('verification_status', 'approved'),
    supabase.from('caterers').select('id', { count: 'exact', head: true }).eq('verification_status', 'pending'),
    supabase.from('buyers').select('id', { count: 'exact', head: true }).eq('subscription_status', 'active'),
  ]);
  return {
    open_briefs: openBriefs.count || 0,
    approved_caterers: approvedCaterers.count || 0,
    pending_caterers: pendingCaterers.count || 0,
    active_buyers: activeBuyers.count || 0,
  };
}

// ============================================================
// INTEREST SIGNUPS (coming-soon page)
// ============================================================

export async function signUpForInterest({ email, locale, role_hint, organisation }) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const { error } = await supabase.from('interest_signups').insert({
    email,
    locale: locale || 'en',
    role_hint: role_hint || null,
    organisation: organisation || null,
    source: 'landing-coming-soon',
  });
  if (error) {
    // Duplicate email → friendly message
    if (error.code === '23505') throw new Error("You're already on the list.");
    throw error;
  }
  return true;
}

export async function insertProposal(form, briefId) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const caterer = await getCatererIdForCurrentUser();
  if (!caterer) throw new Error('No caterer record for current user');
  if (caterer.verification_status !== 'approved') {
    throw new Error('Your caterer profile is not yet approved.');
  }
  const payload = proposalFormToDb(form, briefId, caterer.id);
  const { data, error } = await supabase.from('proposals').insert(payload).select('*').single();
  if (error) throw error;
  return data;
}
